import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { MessageKey, useI18n } from '../i18n';

import {
    BREATH_PATTERN_IDS,
    BreathPatternId,
    orbScale,
    stepAt,
} from './breathing';
import { PageShell } from './components/PageShell';
import { buzz, playCue } from './feedback';

const PHASE_KEY: Record<string, MessageKey> = {
    inhale: 'breathe.inhale',
    hold: 'breathe.hold',
    exhale: 'breathe.exhale',
    rest: 'breathe.rest',
};

/** A guided breathing pause. Purely local: nothing is stored, nothing is sent. */
export const BreathingPage: React.FC = () => {
    const { t } = useI18n();
    const [pattern, setPattern] = useState<BreathPatternId>('box');
    const [running, setRunning] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const lastIndex = useRef(-1);

    useEffect(() => {
        if (!running) return;
        const started = performance.now();
        lastIndex.current = -1;
        let frame = 0;
        const loop = () => {
            const seconds = (performance.now() - started) / 1000;
            const { index } = stepAt(pattern, seconds);
            if (index !== lastIndex.current) {
                // A short tick each time the phase changes.
                if (lastIndex.current !== -1) buzz(15);
                lastIndex.current = index;
            }
            setElapsed(seconds);
            frame = requestAnimationFrame(loop);
        };
        frame = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frame);
    }, [running, pattern]);

    const current = running ? stepAt(pattern, elapsed) : null;
    const scale = running ? orbScale(pattern, elapsed) : 0;
    const remaining = current
        ? Math.max(1, Math.ceil(current.step.seconds * (1 - current.progress)))
        : 0;

    return (
        <PageShell to="/">
            <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {t('breathe.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {t('breathe.intro')}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    value={pattern}
                    aria-label={t('breathe.patternAria')}
                    onChange={(_, value: BreathPatternId | null) => {
                        if (!value) return;
                        playCue('ui');
                        setPattern(value);
                        setRunning(false);
                    }}
                >
                    {BREATH_PATTERN_IDS.map((id) => (
                        <ToggleButton key={id} value={id}>
                            {t(`breathe.pattern.${id}` as MessageKey)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Box
                    sx={{
                        height: 260,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        data-testid="breath-orb"
                        sx={{
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background:
                                'radial-gradient(circle at 35% 30%, rgba(240,214,150,.9), rgba(212,175,106,.35) 60%, rgba(120,80,200,.25))',
                            boxShadow: '0 0 40px rgba(212,175,106,.45)',
                            transform: `scale(${0.4 + 0.6 * scale})`,
                            '@media (prefers-reduced-motion: reduce)': {
                                boxShadow: 'none',
                            },
                        }}
                    />
                </Box>
                <Typography
                    variant="h5"
                    aria-live="polite"
                    sx={{ minHeight: 36, mb: 2 }}
                >
                    {current
                        ? `${t(PHASE_KEY[current.step.phase])} · ${remaining}`
                        : t('breathe.ready')}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => {
                        playCue('ui');
                        setElapsed(0);
                        setRunning((r) => !r);
                    }}
                >
                    {running ? t('breathe.stop') : t('breathe.start')}
                </Button>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 3 }}
                >
                    {t('breathe.note')}
                </Typography>
            </Box>
        </PageShell>
    );
};
